import { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../config";

/**
 * Loads the partner logo for the active link as a blob URL so it renders
 * reliably in dev (ngrok) and production. Uses the link-secured widget
 * endpoint (GET /widget/links/:linkId/logo). The endpoint itself is the source
 * of truth for whether a logo exists: a 404 simply means no logo.
 *
 * `settled` is true only after GET /logo for the current `linkId` resolves
 * (success or 404). While `linkId` is missing it stays false so the splash
 * gate does not treat the pre-claim frame as "no logo".
 *
 * Favicon is handled separately by `usePartnerFavicon` at the app root.
 */
export function usePartnerLogoSrc(linkId: string | undefined) {
  const [src, setSrc] = useState<string | null>(null);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!linkId) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setSrc(null);
      setSettled(false);
      /* eslint-enable react-hooks/set-state-in-effect */
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;
    setSettled(false);

    const load = async () => {
      try {
        const apiBase = config.API_BASE_URL.replace(/\/$/, "");
        const headers: Record<string, string> = { "x-link-id": linkId };
        if (apiBase.includes("ngrok")) {
          headers["ngrok-skip-browser-warning"] = "true";
        }

        const response = await axios.get(
          `${apiBase}/widget/links/${linkId}/logo`,
          { responseType: "blob", headers },
        );

        if (cancelled) return;

        const logoBlob = response.data as Blob;
        objectUrl = URL.createObjectURL(logoBlob);
        setSrc(objectUrl);
      } catch {
        if (!cancelled) {
          setSrc(null);
        }
      } finally {
        if (!cancelled) {
          setSettled(true);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [linkId]);

  return { src, settled };
}
